import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../api/client";
import useAuth from "../../hooks/useAuth";
import "./AuditLogPage.css";

// The MCP endpoint sits next to the API at the server root: ".../api" -> ".../mcp".
function mcpUrl() {
  const base = new URL(api.defaults.baseURL, window.location.origin);
  return `${base.origin}/mcp`;
}

function formatDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function tokenState(token) {
  if (token.revokedAt) return "Revoked";
  if (new Date(token.expiresAt) <= new Date()) return "Expired";
  return "Active";
}

export default function ApiTokensPage({ backTo = "/dashboard", backLabel = "Dashboard" }) {
  const { user } = useAuth();
  const [tokens, setTokens] = useState([]);
  const [name, setName] = useState("");
  const [expiresInDays, setExpiresInDays] = useState(90);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/api-tokens");
      setTokens(res.data?.data || []);
    } catch (err) {
      setError("Failed to load tokens");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await api.post("/api-tokens", { name, expiresInDays });
      setCreated(res.data.data);
      setCopied(false);
      setName("");
      load();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create token");
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (token) => {
    const whose = token.isMine ? "" : ` (${token.owner})`;
    if (!window.confirm(`Revoke "${token.name}"${whose}? Anything using it stops working immediately.`)) return;
    try {
      await api.delete(`/api-tokens/${token.id}`);
      if (created?.id === token.id) setCreated(null);
      load();
    } catch (err) {
      setError("Failed to revoke token");
    }
  };

  const command = created
    ? `claude mcp add --transport http decisionflow ${mcpUrl()} --header "Authorization: Bearer ${created.token}"`
    : "";

  const copyCommand = async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
    } catch (err) {
      setCopied(false);
    }
  };

  return (
    <div className="al-page">
      <Link className="back-link" to={backTo}>
        ← Back to {backLabel}
      </Link>

      <div className="al-header">
        <div>
          <h1>AI Access</h1>
          <p>
            Let an AI assistant like Claude read your projects, stages and
            tasks. Access is read-only and sees only what you can see.
            {user?.isAdmin
              ? " As an admin, you also see and can revoke everyone's connections in this workspace."
              : ""}
          </p>
        </div>
      </div>

      <div className="al-detail" style={{ marginBottom: 14 }}>
        <strong>Connect by signing in (recommended)</strong>
        <p className="al-sub">
          Run this, then type /mcp in Claude Code and choose Authenticate. You
          will be asked to sign in here and allow access.
        </p>
        <div className="al-snapshots">
          <pre>{`claude mcp add --transport http decisionflow ${mcpUrl()}`}</pre>
        </div>
        <p className="al-sub">
          For tools that cannot sign in, create a token below instead.
        </p>
      </div>

      {error ? <div className="al-error">{error}</div> : null}

      <form className="al-filters" onSubmit={handleCreate}>
        <label>
          Token name
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Claude on my laptop"
            maxLength={80}
            required
          />
        </label>
        <label>
          Expires in
          <select
            value={expiresInDays}
            onChange={(e) => setExpiresInDays(Number(e.target.value))}
          >
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={365}>1 year</option>
          </select>
        </label>
        <div className="al-filter-actions">
          <button type="submit" className="al-btn" disabled={busy || !name.trim()}>
            Create token
          </button>
        </div>
      </form>

      {created ? (
        <div className="al-detail" style={{ marginBottom: 14 }}>
          <strong>Copy this now — it won't be shown again.</strong>
          <p className="al-sub">Run this in a terminal to connect Claude Code:</p>
          <div className="al-snapshots">
            <pre>{command}</pre>
          </div>
          <button type="button" className="al-btn secondary" onClick={copyCommand}>
            {copied ? "Copied" : "Copy command"}
          </button>
        </div>
      ) : null}

      <div className="al-table-wrap">
        <table className="al-table">
          <thead>
            <tr>
              <th>Name</th>
              {user?.isAdmin ? <th>Person</th> : null}
              <th>Token</th>
              <th>Status</th>
              <th>Last used</th>
              <th>Expires</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {tokens.length === 0 ? (
              <tr>
                <td colSpan={user?.isAdmin ? 7 : 6} className="al-empty">
                  No tokens yet.
                </td>
              </tr>
            ) : (
              tokens.map((token) => {
                const state = tokenState(token);
                return (
                  <tr key={token.id}>
                    <td>
                      {token.name}
                      {token.viaSignIn ? <div className="al-sub">Connected by signing in</div> : null}
                    </td>
                    {user?.isAdmin ? (
                      <td>{token.isMine ? "You" : token.owner}</td>
                    ) : null}
                    <td className="al-nowrap">
                      <code>{token.prefix}…</code>
                    </td>
                    <td className={state === "Active" ? "" : "al-sub"}>{state}</td>
                    <td className="al-nowrap">{formatDate(token.lastUsedAt)}</td>
                    <td className="al-nowrap">{formatDate(token.expiresAt)}</td>
                    <td>
                      {state === "Active" ? (
                        <button
                          type="button"
                          className="al-btn link"
                          onClick={() => handleRevoke(token)}
                        >
                          Revoke
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
